import * as Telecom from '@servicetitan/telecom-rtcs-client';
import { userStatsFromAgentStats } from '../../types/ServiceUser';
import appManager from '@app/appManager';
import {
  ServiceSessionEventType,
  ServiceSessionUserEvent,
} from '../../types/ServiceEvents';
import requireUser, { rosterUser } from '../../lib/requireUser';
import log from '../../lib/log';
import { serviceState } from '../../lib/state';
import isValidUser from '../../lib/isValidUser';
import { EventAvailabilityState } from '../../lib/eventAvailabilityState';
import {
  ServiceUserUtils,
  userStatsLastActionTimeFix,
} from '../../lib/serviceUserUtils';
import { RTCSEventType } from '../../../metrics/rtcsMetrics';
import { trackRTCSEventLatency } from '../../lib/trackRTCSEventLatency';
import {
  applyAgentSessionNow,
  enqueueNonSelfAgentSessionEvent,
  shouldBufferNonSelfAgentSession,
} from '../../lib/coalesceWorkspaceEvents';

/**
 * The delivered event indicates that a session was successfully
 * delivered to a recipient and is now being handled.
 * @param event
 */
async function prepareAgentSessionDelivered(
  event: Telecom.AgentSessionDeliveredEvent,
) {
  if (!isValidUser(event.agentId)) {
    return undefined;
  }

  const user = await requireUser(event.agentId);
  return { user };
}

function persistDeliveredSession(event: Telecom.AgentSessionDeliveredEvent) {
  const session = serviceState.sessions[event.conversationId];
  if (!session) {
    return;
  }

  session.agentId = event.agentId;
  session.queueId = event.queueId;
  session.queueName = event.queueName;
}

function commitAgentSessionDelivered(
  event: Telecom.AgentSessionDeliveredEvent,
  prepared: NonNullable<
    Awaited<ReturnType<typeof prepareAgentSessionDelivered>>
  >,
  receivedAt: number,
) {
  const user = rosterUser(event.agentId, prepared.user);
  log('rtcs:agent:session_delivered:' + user.name, event);

  user.stats = userStatsFromAgentStats(event.stats);

  userStatsLastActionTimeFix({ user, timestamp: event.timestamp });

  ServiceUserUtils.setEventActivityStatus(user, EventAvailabilityState.OnACall);

  appManager.events.emit<ServiceSessionUserEvent>(
    ServiceSessionEventType.DELIVERED,
    {
      sessionId: event.conversationId,
      sessionSid: event.conversationSid,
      agentId: event.agentId,
      userId: user.id,
    },
  );

  const session = serviceState.sessions[event.conversationId];
  if (!session) {
    console.error('session not found', event.conversationId);
    return;
  }

  persistDeliveredSession(event);
  session.createdAt = receivedAt;
}

async function handleAgentSessionDelivered(
  event: Telecom.AgentSessionDeliveredEvent,
) {
  const receivedAt = Date.now();
  trackRTCSEventLatency(event.timestamp, RTCSEventType.SESSION_DELIVERED);
  if (!shouldBufferNonSelfAgentSession(event.agentId)) {
    return applyAgentSessionNow(event.agentId, (user) => {
      commitAgentSessionDelivered(event, { user }, receivedAt);
    });
  }
  return enqueueNonSelfAgentSessionEvent(event.agentId, {
    prepare: () => prepareAgentSessionDelivered(event),
    commit: (value) => commitAgentSessionDelivered(event, value, receivedAt),
    persistSession: () => persistDeliveredSession(event),
  });
}

export default handleAgentSessionDelivered;
